// Pure totals helpers (display-side only). Never mutate invoice data; saved
// `total_sum_values` shape is unchanged (Alabus/Epona contract intact).

// Parse a money string ("1'234.55", "1234,55", 1234.55) to a Number.
// Returns NaN for empty / non-numeric input so callers can skip invalid lines.
export const parseMoney = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  const num = parseFloat(String(value).replace(/'/g, "").replace(",", "."));
  return isNaN(num) ? NaN : num;
};

// Quantity defaults to 1 when empty/invalid so a blank qty never zeroes a line.
// Used only for aggregate totals — the per-line hint must NOT use this default.
export const parseQuantity = (value) => {
  const num = parseMoney(value);
  return isNaN(num) || num <= 0 ? 1 : num;
};

// Experimental only (enableQuantityTotals). Prefer Item Price alone for real totals:
// extraction usually fills Item Price with the invoice Betrag (extended amount), not
// unit price — so price×qty double-counts (e.g. qty 2 × Betrag 52.40 → 104.80).
export const getLineTotal = (item) => {
  const price = parseMoney(item?.["Item Price"]);
  if (isNaN(price)) return NaN;
  return price * parseQuantity(item?.["Item Quantity"]);
};

// Flatten every line item across all pages and treatments.
export const getAllItems = (invoiceData) => {
  const items = [];
  (invoiceData?.extracted_invoice_data || []).forEach((pageData) => {
    (pageData?.treatments || []).forEach((treatment) => {
      (treatment?.Items || []).forEach((item) => items.push(item));
    });
  });
  return items;
};

// Quantity-aware grand total across every page/treatment.
export const computeQuantityAwareTotal = (invoiceData) =>
  getAllItems(invoiceData).reduce((acc, item) => {
    const lineTotal = getLineTotal(item);
    return isNaN(lineTotal) ? acc : acc + lineTotal;
  }, 0);

export const normalizeVatRate = (value) => {
  if (value === null || value === undefined || value === "") return "none";
  return String(value).trim();
};

// Group line amounts by VAT rate -> { rows: [{rateKey, rateNum, net, vat, gross}], totals }.
// Uses Item Price as the line amount (NOT price × qty). In this product Item Price is
// usually already the extended Betrag from the invoice; multiplying by quantity double-counts.
// Amounts are treated as net (VAT-exclusive): vat = net × rate/100, gross = net + vat.
export const computeVatBreakdown = (invoiceData) => {
  const groups = {};
  getAllItems(invoiceData).forEach((item) => {
    const lineAmount = parseMoney(item?.["Item Price"]);
    if (isNaN(lineAmount)) return;
    const rateKey = normalizeVatRate(item?.["VAT"]);
    const rateNum = rateKey === "none" ? 0 : parseMoney(rateKey) || 0;
    if (!groups[rateKey]) {
      groups[rateKey] = { rateKey, rateNum, net: 0, vat: 0, gross: 0 };
    }
    groups[rateKey].net += lineAmount;
  });
  const rows = Object.values(groups).map((g) => {
    const vat = g.net * (g.rateNum / 100);
    return { ...g, vat, gross: g.net + vat };
  });
  // numeric rates ascending, "none" last
  rows.sort((a, b) => {
    if (a.rateKey === "none") return 1;
    if (b.rateKey === "none") return -1;
    return a.rateNum - b.rateNum;
  });
  const totals = rows.reduce(
    (acc, r) => ({
      net: acc.net + r.net,
      vat: acc.vat + r.vat,
      gross: acc.gross + r.gross,
    }),
    { net: 0, vat: 0, gross: 0 }
  );
  return { rows, totals };
};

export const formatMoney = (value) =>
  isNaN(value) ? "0.00" : Number(value).toFixed(2);
